import React, {useEffect, useState} from "react";
import {Button, Grid} from "@mui/material";
import Typography from "@mui/material/Typography";
import {Link, useParams} from "react-router-dom";
import axios from "axios";
import SyncIcon from "@mui/icons-material/Sync";

interface EventDetails {
    id: number
    cma_scrape_enabled: boolean
    cma_event_id: number | null
    cma_registration_id: number | null
}

interface ScrapeLog {
    triggeredAt: string
    triggeredBy: string | null
    success: boolean
    maxTpUpdated: number | null
    registrationsUpdated: number | null
    errorMessage: string | null
}

const ACTARRAY = [{name: "Check In", link: "pcs"}, {
    name: "Goblin Stamp Spend",
    link: "goblin-stamps"
}, {name: "Donation Logging", link: "donations"}, {
    name: "Maps",
    link: "maps"
}]
const PREGAMEARRAY = [{name: "Upload Player Questions", link: "questions"}, {
    name: "Upload Character Cards",
    link: "sheets"
}, {name: "Pre-Game Logistics", link: "pregame"}]

export default function LogisticsGrid() {
    const {eventId} = useParams<{eventId: string}>()
    const [event, setEvent] = useState<EventDetails | null>(null)
    const [scraping, setScraping] = useState(false)
    const [scrapeResult, setScrapeResult] = useState<string | null>(null)
    const [latestLog, setLatestLog] = useState<ScrapeLog | null>(null)

    useEffect(() => {
        if (eventId) {
            axios.get(`/api/events/${eventId}`)
                .then(({data}) => setEvent(data))
                .catch(() => {})
            axios.get(`/api/events/${eventId}/cma-scrape-log/latest`)
                .then(({data}) => setLatestLog(data))
                .catch(() => {})
        }
    }, [eventId])

    async function runCmaScrape() {
        if (!eventId) return
        setScraping(true)
        setScrapeResult(null)
        try {
            const {data} = await axios.post(`/api/events/${eventId}/cma-scrape`)
            setScrapeResult(`Scrape complete — Max TP: ${data.maxTp ?? 'unchanged'}, Registrations updated: ${data.questionsProcessed}`)
        } catch (err: any) {
            setScrapeResult(`Scrape failed: ${err.response?.data?.error ?? err.message}`)
        } finally {
            setScraping(false)
            axios.get(`/api/events/${eventId}/cma-scrape-log/latest`)
                .then(({data}) => setLatestLog(data))
                .catch(() => {})
        }
    }

    function formatScrapeLog(log: ScrapeLog): string {
        const when = new Date(log.triggeredAt).toLocaleString()
        const who = log.triggeredBy ? `by ${log.triggeredBy}` : 'automatic'
        if (!log.success) return `${when} (${who}) — Failed: ${log.errorMessage ?? 'unknown error'}`
        const parts = [`${when} (${who})`]
        if (log.maxTpUpdated != null) parts.push(`Max TP: ${log.maxTpUpdated}`)
        if (log.registrationsUpdated != null) parts.push(`Registrations: ${log.registrationsUpdated}`)
        return parts.join(' — ')
    }

    const cmaConfigured = event?.cma_event_id != null && event?.cma_registration_id != null
    const showCmaButton = cmaConfigured && event?.cma_scrape_enabled

    return <React.Fragment>
        <Grid container spacing={2} p={2}>
            {ACTARRAY.map((action) => {
                return <Grid item md={4} sm={6} xs={12}><Button id={"action-butt-" + action.name} variant="contained"
                                                                sx={{
                                                                    height: "100%",
                                                                    width: "100%",
                                                                    stretch: "100%",
                                                                    textAlign: "center"
                                                                }} component={Link}
                                                                to={action.link}><Typography>{action.name}</Typography></Button></Grid>
            })}</Grid>
        <Grid container spacing={2} p={2}>
            {PREGAMEARRAY.map((action) => {
                return <Grid item md={4} sm={6} xs={12}><Button id={"action-butt-" + action.name} color={"warning"}
                                                                variant="contained" sx={{
                    height: "100%",
                    width: "100%",
                    stretch: "100%",
                    textAlign: "center"
                }} component={Link} to={action.link}><Typography>{action.name}</Typography></Button></Grid>
            })}
        </Grid>
        {showCmaButton && (
            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<SyncIcon/>}
                        disabled={scraping}
                        onClick={runCmaScrape}
                        sx={{width: "100%"}}
                    >
                        <Typography>{scraping ? 'Running CMA Scrape…' : 'Run CMA Scrape'}</Typography>
                    </Button>
                </Grid>
                {scrapeResult && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color={scrapeResult.startsWith('Scrape failed') ? 'error' : 'success.main'}>
                            {scrapeResult}
                        </Typography>
                    </Grid>
                )}
                {latestLog && !scrapeResult && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color={latestLog.success ? 'text.secondary' : 'error'}>
                            Last scrape: {formatScrapeLog(latestLog)}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        )}
    </React.Fragment>
}